import { lazy, type FC } from 'react';
import * as translator from '../../../utils/translator';
import { ADYEN_ERROR_STATUS_CODES } from '../paymentConstants';
import type { AdyenInitData } from '../paymentTypes';
import type { CheckoutFormProps, CheckoutProviderAdapter } from './types';

// SEPA position A/B test variation: move paybybank right after paypal.
export function reorderSepaAfterPaypal(gatewayResult: any): any {
  const paymentMethods = gatewayResult?.paymentMethods;
  if (!paymentMethods) return gatewayResult;

  const payByBankIdx = paymentMethods.findIndex((pm: any) => pm.type === 'paybybank');
  const paypalIdx = paymentMethods.findIndex((pm: any) => pm.type === 'paypal');

  if (payByBankIdx === -1 || paypalIdx === -1 || payByBankIdx === paypalIdx + 1) {
    return gatewayResult;
  }

  const reordered = [...paymentMethods];
  const [payByBank] = reordered.splice(payByBankIdx, 1);
  const newPaypalIdx = reordered.findIndex((pm: any) => pm.type === 'paypal');
  reordered.splice(newPaypalIdx + 1, 0, payByBank);

  return { ...gatewayResult, paymentMethods: reordered };
}

const loadAdyenForm = () => import('../CheckoutForms/AdyenCheckoutForm');

const AdyenSectionForm = lazy(async () => {
  const { default: AdyenCheckoutForm } = await loadAdyenForm();
  const Form: FC<CheckoutFormProps<AdyenInitData>> = ({ initData, variant, hideBillingCountry, hideExpressCheckout, ...props }) => (
    <AdyenCheckoutForm adyenInitData={initData} {...props} />
  );
  return { default: Form };
});

export const adyenAdapter: CheckoutProviderAdapter<AdyenInitData> = {
  code: 'adyen',

  buildInitData: (dto, { isSubscription, sepaPositionVariant, reorderedPaymentMethods }) => {
    let gatewayResult = dto.gatewayResult;
    let reorderedGatewayResult = dto.reorderedGatewayResult;
    if (sepaPositionVariant === 'variation') {
      gatewayResult = reorderSepaAfterPaypal(dto.gatewayResult);
      if (dto.reorderedGatewayResult) {
        reorderedGatewayResult = reorderSepaAfterPaypal(dto.reorderedGatewayResult);
      }
    }
    return {
      gatewayResult,
      reorderedGatewayResult,
      clientKey: dto.clientKey,
      environment: dto.environment,
      isSubscription,
      orderAddresses: dto.orderAddresses,
      customerData: dto.customerData,
      reorderedPaymentMethods,
    };
  },

  applyAllowList: (init, native) => ({
    ...init,
    gatewayResult: {
      ...init.gatewayResult,
      paymentMethods: (init.gatewayResult?.paymentMethods ?? []).filter((method: any) => native.includes(method.type)),
      storedPaymentMethods: [],
    },
    reorderedGatewayResult: undefined,
    reorderedPaymentMethods: false,
  }),

  loadSdk: loadAdyenForm,
  Form: AdyenSectionForm,

  interpretResult: (result, language) => {
    let message = translator.translate(language, 'payment', 'adyen/refusalCode/DEFAULT');
    if (result.refusalReasonCode) {
      const refusalCode = Number(result.refusalReasonCode);
      message = translator.translate(language, 'payment', `adyen/refusalCode/${refusalCode}`) || message;
    }
    return { message };
  },

  interpretError: (errorResponse, language) => {
    const status = errorResponse?.status;
    if (!status || !ADYEN_ERROR_STATUS_CODES.includes(Number(status))) return undefined;
    return {
      message: translator.translate(language, 'payment', 'adyen/refusalCode/DEFAULT'),
      reason: 'adyen refusal',
      resetDropin: true,
    };
  },

  readRedirectReturn: (search) => ({ redirectResult: search.get('redirectResult') }),

  contextOverrides: () => ({ paymentServiceProvider: 'adyen', checkoutProvider: null }),
};
